import React, { useEffect, useState } from 'react';
import { leadsAPI, applicationsAPI, followupsAPI, api } from '@/utils/api';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Mail, Phone, Edit, Eye, Grid3x3, List, ArrowUpDown, FileText, Calendar, CheckCircle, Clock, Bell, Upload, Download, Trash2, File, FileImage, FileSpreadsheet, Paperclip, X, Archive, ArchiveRestore, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import NotesTimeline from '@/components/leads/NotesTimeline';
import LeadEligibilityPanel from '@/components/leads/LeadEligibilityPanel';
import LeadPricingPanel from '@/components/leads/LeadPricingPanel';
import LeadTvcQuotePanel from '@/components/leads/LeadTvcQuotePanel';
import LeadApplicationPanel from '@/components/leads/LeadApplicationPanel';
import ConstructionDetailsForm, { emptyConstruction, fromRecord, toPayload, hasConstructionData } from '@/components/leads/ConstructionDetailsForm';
import ConstructionDetailsView from '@/components/leads/ConstructionDetailsView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const CLOSED_STAGES = ['Closed', 'Not Interested', 'Closed / Funded', 'Lost / Not Qualified'];
const PIPELINE_STAGES = ['New Lead', 'Contacted', 'Information Needed', 'Ready for Lender Matching', 'Lenders Matched', 'Sourcing / Lender Follow-Up', 'Term Sheet Sent', 'Term Sheet Signed', 'Application Sent', 'Processing', 'Clear to Close (CTC)', 'Closed / Funded', 'Lost / Not Qualified'];
const Leads = () => {
  const navigate = useNavigate();
  const outletCtx = useOutletContext() || {};
  const isAdmin = outletCtx.user?.role === 'admin';
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showAppDialog, setShowAppDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadMatches, setLeadMatches] = useState([]);
  const [matchingInProgress, setMatchingInProgress] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [viewMode, setViewMode] = useState('cards');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [leadFollowups, setLeadFollowups] = useState([]);
  const [showFollowupDialog, setShowFollowupDialog] = useState(false);
  const [followupFormData, setFollowupFormData] = useState({
    scheduled_date: '',
    type: 'call',
    notes: ''
  });
  const [appFormData, setAppFormData] = useState({
    borrower_name: '',
    email: '',
    phone: '',
    property_address: '',
    loan_purpose: '',
    loan_amount: '',
    property_value: '',
    credit_score: '',
    down_payment: ''
  });
  const [construction, setConstruction] = useState(emptyConstruction());
  const [showConstruction, setShowConstruction] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    reason_for_capital: '',
    property_address: '',
    property_type: '',
    amount_needed: '',
    fico_range: '',
    lead_status: 'New Lead',
    source: '',
    initial_note: ''
  });
  const [leadDocuments, setLeadDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [leadEmails, setLeadEmails] = useState([]);
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    subject: '',
    message: ''
  });
  const [emailAttachments, setEmailAttachments] = useState([]);
  const [leadView, setLeadView] = useState('active'); // active, closed, archived
  const [deleteDialog, setDeleteDialog] = useState({ open: false, lead: null });
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    if (leadView === 'archived') {
      fetchArchivedLeads();
    }
  }, [leadView]);

  const fetchArchivedLeads = async () => {
    setLoadingArchived(true);
    try {
      const response = await api.get('/leads/archived');
      setArchivedLeads(response.data);
    } catch (error) {
      console.error('Failed to fetch archived leads:', error);
      toast.error('Failed to load archived leads');
    } finally {
      setLoadingArchived(false);
    }
  };

  useEffect(() => {
    // For archived view, use archivedLeads
    if (leadView === 'archived') {
      let filtered = archivedLeads;
      
      // Filter by search query
      if (searchQuery) {
        filtered = filtered.filter(lead =>
          lead.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.phone?.includes(searchQuery) ||
          lead.property_address?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      // Sort
      filtered.sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        if (sortBy === 'created_at' || sortBy === 'updated_at' || sortBy === 'archived_at') {
          aVal = new Date(aVal || 0);
          bVal = new Date(bVal || 0);
        }
        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
      });
      
      setFilteredLeads(filtered);
      return;
    }
    
    // For active/closed views, use regular leads (excluding archived)
    let filtered = leads.filter(lead => {
      if (lead.archived) return false; // Never show archived in active/closed tabs
      
      if (leadView === 'active') {
        return !CLOSED_STAGES.includes(lead.lead_status);
      } else if (leadView === 'closed') {
        return CLOSED_STAGES.includes(lead.lead_status);
      }
      return true;
    });
    
    // Then filter by search query
    filtered = filtered.filter(lead =>
      lead.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort leads
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'name') {
        aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
        bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else if (sortBy === 'status') {
        aVal = a.lead_status;
        bVal = b.lead_status;
      } else if (sortBy === 'created_at') {
        aVal = new Date(a.created_at);
        bVal = new Date(b.created_at);
      } else if (sortBy === 'amount') {
        aVal = a.amount_needed || '';
        bVal = b.amount_needed || '';
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredLeads(filtered);
  }, [searchQuery, leads, sortBy, sortOrder, leadView]);

  const fetchLeads = async () => {
    try {
      const response = await leadsAPI.getAll();
      setLeads(response.data);
      setFilteredLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      await leadsAPI.delete(leadId);
      toast.success('Lead deleted successfully');
      setDeleteDialog({ open: false, lead: null });
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const handleArchiveLead = async (lead) => {
    try {
      await api.post(`/leads/${lead.lead_id}/archive`);
      toast.success(`${lead.first_name} ${lead.last_name} moved to archive`);
      fetchLeads();
      if (selectedLead?.lead_id === lead.lead_id) {
        setShowDetailDialog(false);
        setSelectedLead(null);
      }
    } catch (error) {
      toast.error('Failed to archive lead');
    }
  };

  const handleUnarchiveLead = async (lead) => {
    try {
      await api.post(`/leads/${lead.lead_id}/unarchive`);
      toast.success(`${lead.first_name} ${lead.last_name} restored to active leads`);
      fetchArchivedLeads();
      fetchLeads();
      if (selectedLead?.lead_id === lead.lead_id) {
        setShowDetailDialog(false);
        setSelectedLead(null);
      }
    } catch (error) {
      toast.error('Failed to restore lead');
    }
  };

  // Counts for tabs
  const activeCount = leads.filter(l => !l.archived && !CLOSED_STAGES.includes(l.lead_status)).length;
  const closedCount = leads.filter(l => !l.archived && CLOSED_STAGES.includes(l.lead_status)).length;
  const archiveCount = archivedLeads.length;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = showConstruction && hasConstructionData(construction) ? { ...formData, construction: toPayload(construction) } : formData;
      if (editingLead) {
        await leadsAPI.update(editingLead.lead_id, payload);
        toast.success('Lead updated successfully');
      } else {
        await leadsAPI.create(payload);
        toast.success('Lead created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingLead ? 'Failed to update lead' : 'Failed to create lead'));
    }
  };

  const resetForm = () => {
    setConstruction(emptyConstruction());
    setShowConstruction(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      reason_for_capital: '',
      property_address: '',
      property_type: '',
      amount_needed: '',
      fico_range: '',
      lead_status: 'New Lead',
      source: '',
      initial_note: ''
    });
    setEditingLead(null);
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setConstruction(fromRecord(lead.construction));
    setShowConstruction(!!lead.construction);
    setFormData({
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email,
      phone: lead.phone,
      reason_for_capital: lead.reason_for_capital,
      property_address: lead.property_address || '',
      property_type: lead.property_type || '',
      amount_needed: lead.amount_needed || '',
      fico_range: lead.fico_range || '',
      lead_status: lead.lead_status,
      source: lead.source || '',
      initial_note: ''
    });
    setShowDialog(true);
  };

  const handleViewDetails = async (lead) => {
    setSelectedLead(lead);
    setShowDetailDialog(true);
    
    // Fetch follow-ups for this lead
    try {
      const response = await followupsAPI.getAll();
      const filtered = response.data.filter(f => f.lead_id === lead.lead_id);
      setLeadFollowups(filtered);
    } catch (error) {
      console.error('Failed to load follow-ups');
    }
    
    // Fetch documents for this lead
    try {
      const docsResponse = await api.get(`/leads/${lead.lead_id}/documents`);
      setLeadDocuments(docsResponse.data);
    } catch (error) {
      console.error('Failed to load documents');
      setLeadDocuments([]);
    }
    
    // Fetch emails for this lead
    fetchLeadEmails(lead.lead_id);
    fetchLeadMatches(lead.lead_id);
  };

  const fetchLeadMatches = async (leadId) => {
    try {
      const response = await api.get(`/lead-matching/matches?lead_id=${leadId}`);
      setLeadMatches(response.data.matches || []);
    } catch (error) {
      console.error('Failed to load lender matches');
      setLeadMatches([]);
    }
  };

  const handleFindMatches = async () => {
    if (!selectedLead) return;
    setMatchingInProgress(true);
    try {
      const response = await api.post(`/lead-matching/find-matches/${selectedLead.lead_id}`);
      if (response.data.success) {
        toast.success(`Found ${response.data.matches_found} matching lender programs`);
      } else {
        toast.error(response.data.error || 'No matches found');
      }
      fetchLeadMatches(selectedLead.lead_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to find matches');
    } finally {
      setMatchingInProgress(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedLead) return;
    await uploadDocument(file);
    e.target.value = ''; // Reset file input
  };

  const uploadDocument = async (file) => {
    if (!file || !selectedLead) return;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    
    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      await api.post(
        `/leads/${selectedLead.lead_id}/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded successfully');
      
      // Refresh documents list
      const docsResponse = await api.get(`/leads/${selectedLead.lead_id}/documents`);
      setLeadDocuments(docsResponse.data);
      
      // Refresh lead to update notes
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await uploadDocument(e.dataTransfer.files[0]);
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const response = await api.get(
        `/leads/${selectedLead.lead_id}/documents/${doc.doc_id}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to download document';
      if (errorMsg.includes('not found on server')) {
        toast.error(`File "${doc.original_filename}" is missing from server. Please re-upload the document.`, { duration: 5000 });
      } else {
        toast.error(errorMsg);
      }
    }
  };

  const handleDocumentDelete = async (doc) => {
    if (!confirm(`Delete "${doc.original_filename}"?`)) return;
    
    try {
      await api.delete(`/leads/${selectedLead.lead_id}/documents/${doc.doc_id}`);
      toast.success('Document deleted');
      
      // Refresh documents list
      const docsResponse = await api.get(`/leads/${selectedLead.lead_id}/documents`);
      setLeadDocuments(docsResponse.data);
      
      // Refresh lead to update notes
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getFileIcon = (fileType) => {
    if (['.jpg', '.jpeg', '.png', '.gif'].includes(fileType)) return <FileImage className="h-5 w-5 text-blue-500" />;
    if (['.xls', '.xlsx', '.csv'].includes(fileType)) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
    if (fileType === '.pdf') return <FileText className="h-5 w-5 text-red-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canPreview = (fileType) => {
    return ['.jpg', '.jpeg', '.png', '.gif', '.pdf', '.txt', '.csv'].includes(fileType);
  };

  const handleDocumentPreview = async (doc) => {
    setPreviewDoc(doc);
    setPreviewContent(null);
    setShowPreviewDialog(true);
    
    try {
      const response = await api.get(
        `/leads/${selectedLead.lead_id}/documents/${doc.doc_id}/download?preview=true`,
        { responseType: 'blob' }
      );
      
      const blob = response.data;
      
      if (['.jpg', '.jpeg', '.png', '.gif'].includes(doc.file_type)) {
        // Image preview
        const url = URL.createObjectURL(blob);
        setPreviewContent({ type: 'image', url });
      } else if (doc.file_type === '.pdf') {
        // PDF preview - create blob URL with correct type
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        setPreviewContent({ type: 'pdf', url });
      } else if (doc.file_type === '.txt' || doc.file_type === '.csv') {
        // Text preview
        const text = await blob.text();
        setPreviewContent({ type: 'text', content: text });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to load preview';
      if (errorMsg.includes('not found on server')) {
        setPreviewContent({ type: 'error', message: `File "${doc.original_filename}" is missing from the server. This can happen after a deployment. Please delete this record and re-upload the document.` });
      } else {
        toast.error(errorMsg);
        setShowPreviewDialog(false);
      }
    }
  };

  const closePreview = () => {
    if (previewContent?.url) {
      URL.revokeObjectURL(previewContent.url);
    }
    setPreviewDoc(null);
    setPreviewContent(null);
    setShowPreviewDialog(false);
  };

  const fetchLeadEmails = async (leadId) => {
    try {
      const response = await api.get(`/leads/${leadId}/emails`);
      setLeadEmails(response.data);
    } catch (error) {
      console.error('Failed to load emails');
      setLeadEmails([]);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!selectedLead || !emailFormData.subject || !emailFormData.message) return;
    
    setSendingEmail(true);
    try {
      const formData = new FormData();
      formData.append('subject', emailFormData.subject);
      formData.append('message', emailFormData.message);
      
      // Add attachments
      emailAttachments.forEach((file) => {
        formData.append('files', file);
      });
      
      const response = await api.post(
        `/leads/${selectedLead.lead_id}/emails`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.email_sent) {
        const attachCount = response.data.attachments?.length || 0;
        toast.success(`Email sent successfully${attachCount > 0 ? ` with ${attachCount} attachment(s)` : ''}!`);
      } else {
        toast.success('Email logged (email service not configured)');
      }
      
      setEmailFormData({ subject: '', message: '' });
      setEmailAttachments([]);
      setShowComposeEmail(false);
      fetchLeadEmails(selectedLead.lead_id);
      fetchLeads(); // Refresh to update notes
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAttachmentAdd = (e) => {
    const files = Array.from(e.target.files);
    setEmailAttachments(prev => [...prev, ...files]);
    e.target.value = ''; // Reset input
  };

  const handleAttachmentRemove = (index) => {
    setEmailAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateApplication = (lead) => {
    setSelectedLead(lead);
    setAppFormData({
      borrower_name: `${lead.first_name} ${lead.last_name}`,
      email: lead.email,
      phone: lead.phone,
      property_address: lead.property_address || '',
      loan_purpose: lead.reason_for_capital?.toLowerCase().includes('purchase') ? 'purchase' : 
                    lead.reason_for_capital?.toLowerCase().includes('refinance') ? 'refinance' : 'purchase',
      loan_amount: lead.amount_needed?.replace(/[^0-9]/g, '') || '',
      property_value: '',
      credit_score: lead.fico_range || '',
      down_payment: ''
    });
    setShowAppDialog(true);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();
    try {
      await applicationsAPI.create({
        lead_id: selectedLead.lead_id,
        borrower_name: appFormData.borrower_name,
        email: appFormData.email,
        phone: appFormData.phone,
        property_address: appFormData.property_address,
        loan_purpose: appFormData.loan_purpose,
        loan_amount: parseFloat(appFormData.loan_amount) || 0,
        property_value: parseFloat(appFormData.property_value) || 0,
        credit_score: appFormData.credit_score,
        down_payment: parseFloat(appFormData.down_payment) || null
      });
      toast.success('Application created successfully!');
      setShowAppDialog(false);
    } catch (error) {
      toast.error('Failed to create application');
    }
  };

  const handleScheduleFollowup = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        lead_id: selectedLead.lead_id,
        scheduled_date: new Date(followupFormData.scheduled_date).toISOString(),
        type: followupFormData.type,
        notes: followupFormData.notes
      };
      
      await followupsAPI.create(submitData);
      
      // Add note to lead about scheduled follow-up
      const noteContent = `📅 Follow-up scheduled: ${followupFormData.type.toUpperCase()} on ${new Date(followupFormData.scheduled_date).toLocaleString()}${followupFormData.notes ? `\nNotes: ${followupFormData.notes}` : ''}`;
      await api.post(
        `/leads/${selectedLead.lead_id}/notes`,
        { content: noteContent }
      );
      
      toast.success('Follow-up scheduled and logged in notes');
      setShowFollowupDialog(false);
      setFollowupFormData({ scheduled_date: '', type: 'call', notes: '' });
      
      // Refresh lead data and follow-ups
      const leadsRes = await leadsAPI.getOne(selectedLead.lead_id);
      setSelectedLead(leadsRes.data);
      const followupsRes = await followupsAPI.getAll();
      setLeadFollowups(followupsRes.data.filter(f => f.lead_id === selectedLead.lead_id));
    } catch (error) {
      toast.error('Failed to schedule follow-up');
    }
  };

  const handleCompleteFollowup = async (followupId, followup) => {
    try {
      await followupsAPI.complete(followupId);
      
      // Add completion note to lead
      const noteContent = `✅ Follow-up completed: ${(followup.type || followup.followup_type || 'Follow-up').toUpperCase()} that was scheduled for ${new Date(followup.scheduled_date).toLocaleString()}${followup.notes ? `\nOriginal notes: ${followup.notes}` : ''}`;
      await api.post(
        `/leads/${selectedLead.lead_id}/notes`,
        { content: noteContent }
      );
      
      toast.success('Follow-up marked complete and logged');
      
      // Refresh follow-ups and lead notes
      const followupsRes = await followupsAPI.getAll();
      setLeadFollowups(followupsRes.data.filter(f => f.lead_id === selectedLead.lead_id));
      const leadsRes = await leadsAPI.getOne(selectedLead.lead_id);
      setSelectedLead(leadsRes.data);
    } catch (error) {
      toast.error('Failed to complete follow-up');
    }
  };

  const handleSendFollowupReminder = async (followupId, followup) => {
    try {
      await api.post(`/followups/${followupId}/send-reminder`, {});
      
      // Add reminder note to lead
      const noteContent = `🔔 Reminder sent for ${(followup.type || followup.followup_type || 'Follow-up').toUpperCase()} follow-up scheduled on ${new Date(followup.scheduled_date).toLocaleString()}`;
      await api.post(
        `/leads/${selectedLead.lead_id}/notes`,
        { content: noteContent }
      );
      
      toast.success('Reminder sent and logged');
      
      // Refresh lead notes
      const leadsRes = await leadsAPI.getOne(selectedLead.lead_id);
      setSelectedLead(leadsRes.data);
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-400',
      'New Lead': 'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-400',
      'Contacted': 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
      'Information Needed': 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400',
      'Ready for Lender Matching': 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400',
      'Lenders Matched': 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400',
      'Sourcing / Lender Follow-Up': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-400',
      'Term Sheet Signed': 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400',
      'Clear to Close (CTC)': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400',
      'Closed / Funded': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
      'Lost / Not Qualified': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
      'HOT LEAD': 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400',
      'Engaged': 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400',
      'Follow up': 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-400',
      'Not Interested': 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
      'Closed': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400',
      'Application Sent': 'bg-lime-100 text-lime-700 dark:bg-lime-950 dark:text-lime-400',
      'Sourcing': 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-400',
      'Terms Accepted': 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400',
      'Term Sheet Sent': 'bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400',
      'Pre Approval Letter': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-400',
      'Processing': 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
      'Underwriting': 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-400',
      'Clear to Close': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400',
      'No Answer': 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400'
    };
    return colors[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-primary">Leads</h1>
          <p className="text-muted-foreground mt-2">Manage and track your leads</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-lead-button">
              <Plus className="h-4 w-4" />
              Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className={`${showConstruction ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`} data-testid="lead-form-dialog">
            <DialogHeader>
              <DialogTitle>{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
              <DialogDescription>Enter the lead information below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason_for_capital">Reason for Capital *</Label>
                <Input
                  id="reason_for_capital"
                  value={formData.reason_for_capital}
                  onChange={(e) => setFormData({ ...formData, reason_for_capital: e.target.value })}
                  placeholder="e.g., Fix and Flip, Purchase"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="property_address">Property Address</Label>
                <Input
                  id="property_address"
                  value={formData.property_address}
                  onChange={(e) => setFormData({ ...formData, property_address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount_needed">Amount Needed</Label>
                  <Input
                    id="amount_needed"
                    value={formData.amount_needed}
                    onChange={(e) => setFormData({ ...formData, amount_needed: e.target.value })}
                    placeholder="$100k to $250k"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fico_range">FICO Range</Label>
                  <Input
                    id="fico_range"
                    value={formData.fico_range}
                    onChange={(e) => setFormData({ ...formData, fico_range: e.target.value })}
                    placeholder="650 to 680"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lead_status">Status *</Label>
                  <Select value={formData.lead_status} onValueChange={(value) => setFormData({ ...formData, lead_status: value })}>
                    <SelectTrigger data-testid="lead-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New Lead">New Lead</SelectItem>
                      <SelectItem value="Contacted">Contacted</SelectItem>
                      <SelectItem value="Information Needed">Information Needed</SelectItem>
                      <SelectItem value="Ready for Lender Matching">Ready for Lender Matching</SelectItem>
                      <SelectItem value="Lenders Matched">Lenders Matched</SelectItem>
                      <SelectItem value="Sourcing / Lender Follow-Up">Sourcing / Lender Follow-Up</SelectItem>
                      <SelectItem value="Term Sheet Sent">Term Sheet Sent</SelectItem>
                      <SelectItem value="Term Sheet Signed">Term Sheet Signed</SelectItem>
                      <SelectItem value="Application Sent">Application Sent</SelectItem>
                      <SelectItem value="Processing">Processing</SelectItem>
                      <SelectItem value="Clear to Close (CTC)">Clear to Close (CTC)</SelectItem>
                      <SelectItem value="Closed / Funded">Closed / Funded</SelectItem>
                      <SelectItem value="Lost / Not Qualified">Lost / Not Qualified</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Input
                    id="source"
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    placeholder="Website, Referral, etc."
                  />
                </div>
              </div>

              {!editingLead && (
                <div className="space-y-2">
                  <Label htmlFor="initial_note">Initial Note</Label>
                  <Input
                    id="initial_note"
                    value={formData.initial_note}
                    onChange={(e) => setFormData({ ...formData, initial_note: e.target.value })}
                    placeholder="Add an initial note for this lead"
                  />
                </div>
              )}

              <div className="border rounded-lg p-3 space-y-3" data-testid="construction-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Construction Loan Details</p>
                    <p className="text-xs text-muted-foreground">New Construction intake — borrower/entity, property & project, development scenarios. Used for lender matching.</p>
                  </div>
                  <Button type="button" size="sm" variant={showConstruction ? 'secondary' : 'outline'} onClick={() => setShowConstruction(!showConstruction)} data-testid="construction-toggle">
                    {showConstruction ? 'Hide' : (editingLead?.construction ? 'Edit' : 'Add')} construction details
                  </Button>
                </div>
                {showConstruction && <ConstructionDetailsForm value={construction} onChange={setConstruction} />}
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="lead-form-submit">
                  {editingLead ? 'Update Lead' : 'Create Lead'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* View Tabs */}
      <div className="flex flex-wrap gap-2 border-b pb-4">
        <Button
          variant={leadView === 'active' ? 'default' : 'outline'}
          onClick={() => setLeadView('active')}
          className="gap-2"
          data-testid="active-leads-tab"
        >
          <CheckCircle2 className="h-4 w-4" />
          Active Leads ({activeCount})
        </Button>
        <Button
          variant={leadView === 'closed' ? 'default' : 'outline'}
          onClick={() => setLeadView('closed')}
          className="gap-2"
          data-testid="closed-leads-tab"
        >
          <CheckCircle className="h-4 w-4" />
          Closed Loans ({closedCount})
        </Button>
        <Button
          variant={leadView === 'archived' ? 'default' : 'outline'}
          onClick={() => setLeadView('archived')}
          className="gap-2"
          data-testid="archived-leads-tab"
        >
          <Archive className="h-4 w-4" />
          Archived ({archiveCount})
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search leads by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Date Created</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="status">Status</SelectItem>
              <SelectItem value="amount">Amount</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            data-testid="sort-order-button"
          >
            <ArrowUpDown className="h-4 w-4" />
          </Button>
          
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'cards' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('cards')}
              data-testid="cards-view-button"
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => setViewMode('list')}
              data-testid="list-view-button"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Cards View */}
      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLeads.length === 0 ? (
            <div className="col-span-full text-center py-12 text-muted-foreground">
              No leads found
            </div>
          ) : (
            filteredLeads.map((lead, index) => (
            <Card key={lead.lead_id} className="hover:shadow-lg transition-shadow" data-testid={`lead-card-${index}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {lead.first_name} {lead.last_name}
                    </CardTitle>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${getStatusColor(lead.lead_status)}`}>
                      {lead.lead_status}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleViewDetails(lead)} data-testid="view-lead-details">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(lead)} data-testid="edit-lead-btn">
                      <Edit className="h-4 w-4" />
                    </Button>
                    {leadView === 'archived' ? (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleUnarchiveLead(lead)}
                        className="text-green-600 hover:text-green-700"
                        title="Restore from archive"
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleArchiveLead(lead)}
                        className="text-orange-500 hover:text-orange-600"
                        title="Move to archive"
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setDeleteDialog({ open: true, lead })}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{lead.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{lead.phone}</span>
                  </div>
                  <div className="mt-4">
                    <p className="font-medium">{lead.reason_for_capital}</p>
                    {lead.amount_needed && (
                      <p className="text-muted-foreground">Amount: {lead.amount_needed}</p>
                    )}
                  </div>
                  {lead.notes && lead.notes.length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      <p className="text-xs text-muted-foreground">{lead.notes.length} note{lead.notes.length > 1 ? 's' : ''}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            ))
          )}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>FICO</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                      No leads found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((lead, index) => (
                    <TableRow key={lead.lead_id} data-testid={`lead-row-${index}`}>
                      <TableCell className="font-medium">
                        {lead.first_name} {lead.last_name}
                        {lead.notes && lead.notes.length > 0 && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            ({lead.notes.length} notes)
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="h-3 w-3" />
                          {lead.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {lead.phone || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(lead.lead_status)}`}>
                          {lead.lead_status}
                        </span>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {lead.reason_for_capital}
                      </TableCell>
                      <TableCell>{lead.amount_needed || 'N/A'}</TableCell>
                      <TableCell>{lead.fico_range || 'N/A'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewDetails(lead)}
                            data-testid={`view-details-${index}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(lead)}
                            data-testid={`edit-lead-${index}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {leadView === 'archived' ? (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleUnarchiveLead(lead)}
                              className="text-green-600 hover:text-green-700"
                              title="Restore from archive"
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleArchiveLead(lead)}
                              className="text-orange-500 hover:text-orange-600"
                              title="Move to archive"
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setDeleteDialog({ open: true, lead })}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-lead-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Lead Details Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl w-[calc(100vw-1rem)] sm:w-full max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0">
          {selectedLead && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-3">
                  {selectedLead.first_name} {selectedLead.last_name}
                  {selectedLead.archived && (
                    <span className="bg-orange-500/20 text-orange-600 text-xs px-2 py-1 rounded-full">Archived</span>
                  )}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(selectedLead.lead_status)}`} data-testid="lead-detail-stage-badge">
                      {selectedLead.lead_status}
                    </span>
                    <Select
                      value={PIPELINE_STAGES.includes(selectedLead.lead_status) ? selectedLead.lead_status : ''}
                      onValueChange={async (value) => {
                        try {
                          await leadsAPI.update(selectedLead.lead_id, { lead_status: value });
                          setSelectedLead({ ...selectedLead, lead_status: value });
                          toast.success(`Stage set to ${value}`);
                          fetchLeads();
                        } catch (error) {
                          toast.error('Failed to update stage');
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 w-[240px] text-xs" data-testid="lead-status-select">
                        <SelectValue placeholder="Move to pipeline stage..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PIPELINE_STAGES.map((st, i) => (
                          <SelectItem key={st} value={st} data-testid={`lead-stage-option-${i + 1}`}>{i + 1}. {st}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-wrap justify-end gap-2 mb-4 min-w-0">
                {selectedLead.archived ? (
                  <Button 
                    onClick={() => handleUnarchiveLead(selectedLead)}
                    variant="outline"
                    className="gap-2 text-green-600 border-green-600 hover:bg-green-50"
                  >
                    <ArchiveRestore className="h-4 w-4" />
                    Restore Lead
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleArchiveLead(selectedLead)}
                    variant="outline"
                    className="gap-2 text-orange-500 border-orange-500 hover:bg-orange-50"
                  >
                    <Archive className="h-4 w-4" />
                    Archive Lead
                  </Button>
                )}
                <Button 
                  onClick={async () => {
                    try {
                      const response = await api.post(
                        `/leads/${selectedLead.lead_id}/match-lenders`,
                        {}
                      );
                      setShowDetailDialog(false);
                      navigate('/lender-matching', { 
                        state: { lead: selectedLead, matches: response.data }
                      });
                    } catch (error) {
                      toast.error('Failed to find matching lenders');
                    }
                  }}
                  variant="secondary"
                  className="gap-2"
                >
                  <Search className="h-4 w-4" />
                  Find Matching Lenders
                </Button>
                <Button 
                  onClick={() => { setShowDetailDialog(false); handleCreateApplication(selectedLead); }}
                  className="gap-2"
                  data-testid="create-app-from-lead"
                >
                  <FileText className="h-4 w-4" />
                  Create Application
                </Button>
              </div>

              <Tabs defaultValue="details" className="w-full">
                <TabsList className="flex flex-wrap w-full max-w-full h-auto gap-1 justify-start md:grid md:grid-cols-7" data-testid="lead-detail-tabs">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="matches" data-testid="lead-matches-tab">Lenders{selectedLead.eligibility?.summary ? ` (${selectedLead.eligibility.summary.eligible}/${selectedLead.eligibility.summary.potential})` : ""}</TabsTrigger>
                  <TabsTrigger value="pricing" data-testid="lead-pricing-tab">Pricing</TabsTrigger>
                  <TabsTrigger value="application" data-testid="lead-application-tab">Application</TabsTrigger>
                  <TabsTrigger value="emails">Emails ({leadEmails.length})</TabsTrigger>
                  <TabsTrigger value="notes">Notes ({selectedLead.notes?.length || 0})</TabsTrigger>
                  <TabsTrigger value="documents">Documents ({leadDocuments.length})</TabsTrigger>
                  <TabsTrigger value="followups">Follow-ups</TabsTrigger>
                </TabsList>

                <TabsContent value="pricing" className="mt-4 space-y-6">
                  <section className="space-y-2" data-testid="lead-tvc-section">
                    <h3 className="text-sm font-semibold">Temple View Capital — Sales Tool pricing (Wholesale Broker)</h3>
                    <LeadTvcQuotePanel lead={selectedLead} />
                  </section>
                  <section className="space-y-2 border-t pt-4">
                    <h3 className="text-sm font-semibold">Other lender rate sheets</h3>
                    <LeadPricingPanel lead={selectedLead} />
                  </section>
                </TabsContent>
                <TabsContent value="application" className="mt-4">
                  <LeadApplicationPanel lead={selectedLead} isAdmin={isAdmin} />
                </TabsContent>

                <TabsContent value="matches" className="mt-4 space-y-4" data-testid="lead-matches-panel">
                  <LeadEligibilityPanel lead={selectedLead} onLeadUpdated={fetchLeads} />
                  <details className="border rounded-lg p-3">
                    <summary className="text-sm cursor-pointer text-muted-foreground" data-testid="lead-ai-matches-summary">AI-suggested matches from parsed lender emails ({leadMatches.length})</summary>
                    <div className="mt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Suggestions only — verify against the eligibility results above before relying on them.
                    </p>
                    <Button size="sm" variant="outline" onClick={handleFindMatches} disabled={matchingInProgress} data-testid="lead-find-matches-btn">
                      {matchingInProgress ? 'Matching...' : leadMatches.length ? 'Re-run AI Matching' : 'Run AI Matching'}
                    </Button>
                  </div>
                  {leadMatches.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center" data-testid="lead-matches-empty">No lender matches yet for this lead.</p>
                  ) : (
                    [...leadMatches].sort((a, b) => (b.match_score || 0) - (a.match_score || 0)).map((match) => (
                      <div key={match.match_id} className="border rounded-lg p-3" data-testid={`lead-match-${match.match_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium">{match.lender_name}</p>
                            <p className="text-sm text-muted-foreground">{match.program_name}</p>
                          </div>
                          <span className={`text-sm font-semibold px-2 py-0.5 rounded-full ${match.match_score >= 80 ? 'bg-green-100 text-green-700' : match.match_score >= 60 ? 'bg-amber-100 text-amber-700' : 'bg-zinc-100 text-zinc-700'}`}>
                            {match.match_score}%
                          </span>
                        </div>
                        {match.match_reasons?.length > 0 && (
                          <ul className="mt-2 text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                            {match.match_reasons.slice(0, 4).map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          Matched {match.created_at ? new Date(match.created_at).toLocaleString() : ''} · status: {match.status}
                        </p>
                      </div>
                    ))
                  )}
                    </div>
                  </details>
                </TabsContent>

                <TabsContent value="details" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Email</Label>
                      <p className="font-medium">{selectedLead.email}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Phone</Label>
                      <p className="font-medium">{selectedLead.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Reason for Capital</Label>
                      <p className="font-medium">{selectedLead.reason_for_capital}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Amount Needed</Label>
                      <p className="font-medium">{selectedLead.amount_needed || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">FICO Range</Label>
                      <p className="font-medium">{selectedLead.fico_range || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Property Type</Label>
                      <p className="font-medium">{selectedLead.property_type || 'N/A'}</p>
                    </div>
                  </div>
                  {selectedLead.property_address && (
                    <div>
                      <Label className="text-muted-foreground">Property Address</Label>
                      <p className="font-medium">{selectedLead.property_address}</p>
                    </div>
                  )}
                  {selectedLead.source && (
                    <div>
                      <Label className="text-muted-foreground">Source</Label>
                      <p className="font-medium">{selectedLead.source}</p>
                    </div>
                  )}
                  <ConstructionDetailsView construction={selectedLead.construction} />
                </TabsContent>

                <TabsContent value="emails" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Email Conversations</h3>
                      <Button
                        size="sm"
                        onClick={() => setShowComposeEmail(true)}
                        className="gap-2"
                      >
                        <Mail className="h-4 w-4" />
                        Compose Email
                      </Button>
                    </div>

                    {showComposeEmail && (
                      <Card className="border-primary">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">New Email to {selectedLead.email}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={handleSendEmail} className="space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="email_subject">Subject *</Label>
                              <Input
                                id="email_subject"
                                value={emailFormData.subject}
                                onChange={(e) => setEmailFormData({...emailFormData, subject: e.target.value})}
                                placeholder="Enter email subject"
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="email_message">Message *</Label>
                              <Textarea
                                id="email_message"
                                value={emailFormData.message}
                                onChange={(e) => setEmailFormData({...emailFormData, message: e.target.value})}
                                placeholder="Type your message..."
                                rows={5}
                                required
                              />
                            </div>
                            
                            {/* Attachments */}
                            <div className="space-y-2">
                              <Label>Attachments</Label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="file"
                                  id="email-attachments"
                                  className="hidden"
                                  onChange={handleAttachmentAdd}
                                  multiple
                                />
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => document.getElementById('email-attachments').click()}
                                  className="gap-2"
                                >
                                  <Paperclip className="h-4 w-4" />
                                  Add Attachment
                                </Button>
                                <span className="text-xs text-muted-foreground">
                                  {emailAttachments.length > 0 ? `${emailAttachments.length} file(s) selected` : 'No files attached'}
                                </span>
                              </div>
                              
                              {emailAttachments.length > 0 && (
                                <div className="space-y-1 mt-2">
                                  {emailAttachments.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                                      <div className="flex items-center gap-2">
                                        <Paperclip className="h-3 w-3 text-muted-foreground" />
                                        <span className="truncate max-w-[200px]">{file.name}</span>
                                        <span className="text-xs text-muted-foreground">
                                          ({(file.size / 1024).toFixed(1)} KB)
                                        </span>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleAttachmentRemove(index)}
                                        className="h-6 w-6 p-0"
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            <div className="flex justify-end gap-2">
                              <Button type="button" variant="outline" onClick={() => {setShowComposeEmail(false); setEmailFormData({subject: '', message: ''}); setEmailAttachments([]);}}>
                                Cancel
                              </Button>
                              <Button type="submit" disabled={sendingEmail}>
                                {sendingEmail ? 'Sending...' : 'Send Email'}
                              </Button>
                            </div>
                          </form>
                        </CardContent>
                      </Card>
                    )}

                    {leadEmails.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed rounded-lg">
                        <Mail className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">No emails sent yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Click &quot;Compose Email&quot; to start a conversation</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {leadEmails.map((email) => (
                          <Card key={email.email_id} className="overflow-hidden">
                            <div className={`h-1 ${email.direction === 'outbound' ? 'bg-blue-500' : 'bg-green-500'}`} />
                            <CardContent className="pt-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className={`p-1.5 rounded-full ${email.direction === 'outbound' ? 'bg-blue-100' : 'bg-green-100'}`}>
                                    <Mail className={`h-3 w-3 ${email.direction === 'outbound' ? 'text-blue-600' : 'text-green-600'}`} />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{email.subject}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {email.direction === 'outbound' ? 'To' : 'From'}: {email.to_email}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${email.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                    {email.status}
                                  </span>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {new Date(email.created_at).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                              <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                                <p className="text-sm whitespace-pre-wrap">{email.message}</p>
                              </div>
                              {email.attachments && email.attachments.length > 0 && (
                                <div className="mt-2 flex items-center gap-2 flex-wrap">
                                  <Paperclip className="h-3 w-3 text-muted-foreground" />
                                  {email.attachments.map((att, idx) => (
                                    <span key={idx} className="text-xs bg-muted px-2 py-1 rounded">
                                      {att}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="mt-4">
                  <NotesTimeline
                    leadId={selectedLead.lead_id}
                    notes={selectedLead.notes || []}
                    onNotesUpdate={() => {
                      fetchLeads();
                      leadsAPI.getOne(selectedLead.lead_id).then(res => {
                        setSelectedLead(res.data);
                      });
                    }}
                  />
                </TabsContent>

                <TabsContent value="documents" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Documents</h3>
                      <div>
                        <input
                          type="file"
                          id="doc-upload"
                          className="hidden"
                          onChange={handleDocumentUpload}
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv"
                        />
                        <Button
                          size="sm"
                          onClick={() => document.getElementById('doc-upload').click()}
                          disabled={uploadingDoc}
                          className="gap-2"
                        >
                          <Upload className="h-4 w-4" />
                          {uploadingDoc ? 'Uploading...' : 'Upload Document'}
                        </Button>
                      </div>
                    </div>

                    {/* Drag and Drop Zone */}
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        dragActive 
                          ? 'border-primary bg-primary/5' 
                          : 'border-muted-foreground/25 hover:border-muted-foreground/50'
                      }`}
                    >
                      <Upload className={`h-10 w-10 mx-auto mb-2 ${dragActive ? 'text-primary' : 'text-muted-foreground'}`} />
                      <p className={`text-sm ${dragActive ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                        {dragActive ? 'Drop file here to upload' : 'Drag & drop files here, or click button above'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Supported: PDF, Word, Excel, Images, Text (Max 10MB)
                      </p>
                    </div>

                    {leadDocuments.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground">Uploaded Files ({leadDocuments.length})</h4>
                        {leadDocuments.map((doc) => (
                          <div
                            key={doc.doc_id}
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              {getFileIcon(doc.file_type)}
                              <div>
                                <p className="font-medium text-sm">{doc.original_filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {canPreview(doc.file_type) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDocumentPreview(doc)}
                                  title="Preview"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDocumentDownload(doc)}
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDocumentDelete(doc)}
                                title="Delete"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="followups" className="mt-4">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Follow-ups for this Lead</h3>
                      <Button
                        size="sm"
                        onClick={() => setShowFollowupDialog(true)}
                        className="gap-2"
                        data-testid="schedule-followup-from-lead"
                      >
                        <Plus className="h-4 w-4" />
                        Schedule Follow-up
                      </Button>
                    </div>

                    {leadFollowups.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No follow-ups scheduled for this lead
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {leadFollowups
                          .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
                          .map((followup, index) => {
                            const isOverdue = new Date(followup.scheduled_date) < new Date() && !followup.completed;
                            const isDueToday = new Date(followup.scheduled_date).toDateString() === new Date().toDateString() && !followup.completed;
                            
                            return (
                              <Card 
                                key={followup.followup_id} 
                                className={followup.completed ? 'opacity-60' : ''}
                                data-testid={`lead-followup-${index}`}
                              >
                                <CardContent className="pt-4">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        {followup.completed ? (
                                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400 font-medium">
                                            ✓ COMPLETED
                                          </span>
                                        ) : isOverdue ? (
                                          <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400 font-medium">
                                            OVERDUE
                                          </span>
                                        ) : isDueToday ? (
                                          <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-400 font-medium">
                                            DUE TODAY
                                          </span>
                                        ) : (
                                          <Clock className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                                          {(followup.type || followup.followup_type || 'FOLLOW-UP').toUpperCase()}
                                        </span>
                                      </div>
                                      <p className="text-sm font-medium">
                                        {new Date(followup.scheduled_date).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                      {followup.notes && (
                                        <p className="text-sm text-muted-foreground mt-1">
                                          {followup.notes}
                                        </p>
                                      )}
                                    </div>
                                    {!followup.completed && (
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleSendFollowupReminder(followup.followup_id, followup)}
                                          title="Send Reminder"
                                        >
                                          <Bell className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => handleCompleteFollowup(followup.followup_id, followup)}
                                          title="Mark Complete"
                                        >
                                          <CheckCircle className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Application Dialog */}
      <Dialog open={showAppDialog} onOpenChange={setShowAppDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Application from Lead</DialogTitle>
            <DialogDescription>
              Creating application for {selectedLead?.first_name} {selectedLead?.last_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitApplication} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="app_borrower_name">Borrower Name *</Label>
                <Input
                  id="app_borrower_name"
                  value={appFormData.borrower_name}
                  onChange={(e) => setAppFormData({ ...appFormData, borrower_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_email">Email *</Label>
                <Input
                  id="app_email"
                  type="email"
                  value={appFormData.email}
                  onChange={(e) => setAppFormData({ ...appFormData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_phone">Phone *</Label>
                <Input
                  id="app_phone"
                  value={appFormData.phone}
                  onChange={(e) => setAppFormData({ ...appFormData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_loan_purpose">Loan Purpose *</Label>
                <Select 
                  value={appFormData.loan_purpose} 
                  onValueChange={(value) => setAppFormData({ ...appFormData, loan_purpose: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">Purchase</SelectItem>
                    <SelectItem value="refinance">Refinance</SelectItem>
                    <SelectItem value="cash-out">Cash-Out</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_loan_amount">Loan Amount *</Label>
                <Input
                  id="app_loan_amount"
                  type="number"
                  placeholder="Enter amount in dollars"
                  value={appFormData.loan_amount}
                  onChange={(e) => setAppFormData({ ...appFormData, loan_amount: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_property_value">Property Value</Label>
                <Input
                  id="app_property_value"
                  type="number"
                  placeholder="Enter property value"
                  value={appFormData.property_value}
                  onChange={(e) => setAppFormData({ ...appFormData, property_value: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_credit_score">Credit Score</Label>
                <Input
                  id="app_credit_score"
                  placeholder="e.g., 680 to 720"
                  value={appFormData.credit_score}
                  onChange={(e) => setAppFormData({ ...appFormData, credit_score: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="app_down_payment">Down Payment</Label>
                <Input
                  id="app_down_payment"
                  type="number"
                  placeholder="Enter down payment amount"
                  value={appFormData.down_payment}
                  onChange={(e) => setAppFormData({ ...appFormData, down_payment: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="app_property_address">Property Address *</Label>
              <Textarea
                id="app_property_address"
                placeholder="Enter full property address"
                value={appFormData.property_address}
                onChange={(e) => setAppFormData({ ...appFormData, property_address: e.target.value })}
                rows={2}
                required
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAppDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Create Application
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Follow-up from Lead Dialog */}
      <Dialog open={showFollowupDialog} onOpenChange={setShowFollowupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Follow-up</DialogTitle>
            <DialogDescription>
              Create a follow-up reminder for {selectedLead?.first_name} {selectedLead?.last_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleScheduleFollowup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="followup_date">Scheduled Date & Time *</Label>
              <Input
                id="followup_date"
                type="datetime-local"
                value={followupFormData.scheduled_date}
                onChange={(e) => setFollowupFormData({ ...followupFormData, scheduled_date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="followup_type">Follow-up Type *</Label>
              <Select 
                value={followupFormData.type} 
                onValueChange={(value) => setFollowupFormData({ ...followupFormData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">Phone Call</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="followup_notes">Notes</Label>
              <Textarea
                id="followup_notes"
                value={followupFormData.notes}
                onChange={(e) => setFollowupFormData({ ...followupFormData, notes: e.target.value })}
                placeholder="Add any notes about this follow-up"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowFollowupDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Schedule & Log
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewDoc && getFileIcon(previewDoc.file_type)}
              {previewDoc?.original_filename}
            </DialogTitle>
            <DialogDescription>
              {previewDoc && `${formatFileSize(previewDoc.file_size)} • Uploaded ${new Date(previewDoc.uploaded_at).toLocaleDateString()}`}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            {!previewContent && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
            {previewContent?.type === 'error' && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
                <p className="text-destructive font-medium mb-2">File Not Available</p>
                <p className="text-muted-foreground text-sm max-w-md">{previewContent.message}</p>
                <Button 
                  variant="destructive" 
                  className="mt-4"
                  onClick={() => {
                    closePreview();
                    handleDocumentDelete(previewDoc);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Record
                </Button>
              </div>
            )}
            {previewContent?.type === 'image' && (
              <img 
                src={previewContent.url} 
                alt={previewDoc?.original_filename}
                className="max-w-full h-auto mx-auto rounded-lg"
              />
            )}
            {previewContent?.type === 'pdf' && (
              <iframe
                src={previewContent.url}
                className="w-full h-[65vh] rounded-lg border"
                title={previewDoc?.original_filename}
              />
            )}
            {previewContent?.type === 'text' && (
              <pre className="bg-muted p-4 rounded-lg overflow-auto text-sm whitespace-pre-wrap font-mono">
                {previewContent.content}
              </pre>
            )}
          </div>
          {previewContent?.type !== 'error' && (
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={closePreview}>
                Close
              </Button>
              <Button onClick={() => previewDoc && handleDocumentDownload(previewDoc)}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, lead: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Lead
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the lead for{' '}
              <span className="font-semibold">
                {deleteDialog.lead?.first_name} {deleteDialog.lead?.last_name}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, lead: null })}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteLead(deleteDialog.lead?.lead_id)}
            >
              Delete Lead
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
